import React, { useState, useEffect, useRef, useMemo } from 'react';
import AppIcon from '../common/AppIcon';
import GradientIcon from '../common/GradientIcon';
import { useParams, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { apiService, getImgUrl } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { useQueryClient } from '@tanstack/react-query';

// ----------------------------------------------------------------------
// 子组件：拼写界面 (双通道输入 + 红色错误反馈 + 3次容错)
// ----------------------------------------------------------------------
const SpellingInterface = ({ word, onComplete }) => {
  const [userInput, setUserInput] = useState(''); // 用于显示当前的错误输入
  const [currentLetterIndex, setCurrentLetterIndex] = useState(1); // 从第2个字母开始
  const [errorCount, setErrorCount] = useState(0);
  const [completedLetters, setCompletedLetters] = useState([word[0]]); 
  const [isCompleted, setIsCompleted] = useState(false);
  const [showCompletionMessage, setShowCompletionMessage] = useState(false);
  const [hasAutoFill, setHasAutoFill] = useState(false);

  const inputRef = useRef(null);
  const processingRef = useRef(false); // 互斥锁

  // 初始化聚焦
  useEffect(() => {
    const timer = setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // 🟢 核心逻辑
  const handleLogic = (key) => {
    if (isCompleted || currentLetterIndex >= word.length) return;

    const pressedKey = key.toLowerCase();
    const expectedLetter = word[currentLetterIndex].toLowerCase();

    // 忽略非字母
    if (!pressedKey.match(/^[a-z]$/)) return;

    if (pressedKey === expectedLetter) {
      // ✅ 正确
      const newCompletedLetters = [...completedLetters, word[currentLetterIndex]];
      setCompletedLetters(newCompletedLetters);
      setCurrentLetterIndex(prev => prev + 1);
      setErrorCount(0);
      setUserInput('');

      if (currentLetterIndex + 1 >= word.length) {
        setIsCompleted(true);
        setShowCompletionMessage(true);
      }
    } else {
      // ❌ 错误
      setErrorCount(prev => {
        const newErrorCount = prev + 1;
        
        // 🟢 Feature 8: 允许3次错误 (第4次触发失败/自动填充)
        if (newErrorCount >= 4) {
          setHasAutoFill(true);
          const newCompletedLetters = [...completedLetters, word[currentLetterIndex]];
          setCompletedLetters(newCompletedLetters);
          setCurrentLetterIndex(prevIndex => prevIndex + 1);
          setUserInput('');
          
          if (currentLetterIndex + 1 >= word.length) {
            setIsCompleted(true);
            setShowCompletionMessage(true);
          }
          return 0; 
        }
        return newErrorCount;
      });
      
      // 显示红色错误字母 300ms
      setUserInput(pressedKey);
      setTimeout(() => setUserInput(''), 300);
    }
  };

  // 通道 1: PC 全局键盘
  useEffect(() => {
    const handleWindowKeyDown = (e) => {
      if (isCompleted) return;
      if (e.keyCode === 229 || e.key === 'Unidentified') return;
      if (e.ctrlKey || e.metaKey || e.altKey || e.key.length > 1) return;

      processingRef.current = true;
      handleLogic(e.key);
      setTimeout(() => { processingRef.current = false; }, 50);
    };
    window.addEventListener('keydown', handleWindowKeyDown);
    return () => window.removeEventListener('keydown', handleWindowKeyDown);
  }, [currentLetterIndex, errorCount, isCompleted, word, completedLetters]);

  // 通道 2: 移动端隐形输入框
  const handleInputChange = (e) => {
    const val = e.target.value;
    if (!val) return;
    const char = val.slice(-1);
    e.target.value = '';
    if (!processingRef.current) {
        handleLogic(char);
    }
  };

  return (
    <div className="text-center relative outline-none" onClick={() => inputRef.current?.focus()}>
      {/* 隐形输入框 */}
      <input
        ref={inputRef}
        type="text"
        onChange={handleInputChange}
        className="absolute top-0 left-0 w-full h-full opacity-0 z-10 cursor-default caret-transparent"
        style={{ fontSize: '16px' }}
        autoComplete="off"
        spellCheck="false"
        autoFocus
      />

      <div className="flex justify-center items-center space-x-2 mb-8 relative z-0">
        {word.split('').map((letter, index) => {
          const isLetterCompleted = index < completedLetters.length;
          const isCurrent = index === currentLetterIndex && !showCompletionMessage;
          
          let boxStyle = 'bg-gray-100 border-gray-300 text-gray-400';
          if (isLetterCompleted) {
            boxStyle = 'bg-green-100 border-green-400 text-green-800';
          } else if (isCurrent) {
            if (userInput) {
               // 错误输入显示红色
               boxStyle = 'bg-red-100 border-red-400 text-red-800 animate-shake';
            } else {
               // 等待输入显示黄色高亮
               boxStyle = 'bg-yellow-100 border-yellow-400 text-gray-800 animate-pulse ring-2 ring-yellow-200';
            }
          }

          return (
            <div key={index} className={`w-12 h-16 border-2 rounded-lg flex items-center justify-center text-2xl font-bold transition-all duration-200 ${boxStyle}`}>
              {isLetterCompleted ? letter : (isCurrent && userInput ? userInput : '')}
            </div>
          );
        })}
      </div>

      <div className="mb-6 relative z-0">
        {!showCompletionMessage ? (
          <div className="text-lg text-gray-700">
            {currentLetterIndex < word.length ? (
              <>
                Type the next letter: <span className="font-bold text-blue-600">{currentLetterIndex === 0 ? 'First letter is given' : `Letter ${currentLetterIndex + 1}`}</span>
                {errorCount > 0 && (
                  <div className="text-red-500 text-sm mt-1 font-bold">
                    {errorCount}/3 Mistakes
                  </div>
                )}
              </>
            ) : <div className="text-green-600 font-bold">Word completed!</div>}
          </div>
        ) : (
          <div className="text-green-600 font-bold text-xl animate-bounce">🎉 Spelling completed! Click "Next Word"</div>
        )}
      </div>

      {isCompleted && (
        <div className="mb-6 text-center relative z-20">
          <motion.button 
            whileHover={{ scale: 1.05 }} 
            whileTap={{ scale: 0.95 }} 
            onClick={(e) => { e.stopPropagation(); onComplete(!hasAutoFill); }} 
            className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-6 rounded-full shadow-lg"
          >
            Next Word →
          </motion.button>
        </div>
      )}
      
      <div className="w-full bg-gray-200 rounded-full h-2 mb-4 relative z-0">
        <div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${(completedLetters.length / word.length) * 100}%` }}></div>
      </div>
    </div>
  );
};

// 🟢 [新增] 结算模态框组件
const CompletionModal = ({ isSuccess, score, accuracy, onRetry, onContinue, onExit, newBadges }) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4 animate-in fade-in duration-300">
      <motion.div 
        initial={{ scale: 0.8, opacity: 0, y: 20 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        className="bg-white rounded-3xl p-8 max-w-md w-full text-center shadow-2xl relative overflow-hidden border-4 border-white/50"
      >
        {/* 背景装饰 */}
        <div className={`absolute top-0 left-0 w-full h-32 ${isSuccess ? 'bg-gradient-to-b from-green-400 to-green-50' : 'bg-gradient-to-b from-red-500 to-red-50'}`}></div>
        
        <div className="relative z-10 mt-8">
          {/* 大图标 */}
          <div className={`mx-auto w-24 h-24 rounded-full flex items-center justify-center text-5xl border-8 mb-4 shadow-xl transform transition-transform hover:scale-110 ${isSuccess ? 'bg-green-500 border-green-100 text-white' : 'bg-white border-red-100 text-red-500'}`}>
             {isSuccess ? '🏆' : '💪'}
          </div>

          <h2 className="text-3xl font-black text-slate-800 mb-2 tracking-tight">
            {isSuccess ? 'LESSON COMPLETE!' : 'Don\'t Give Up!'}
          </h2>
          
          <p className="text-slate-500 mb-8 font-medium px-4">
            {isSuccess 
              ? 'Outstanding work, Agent! You have mastered this intelligence.' 
              : 'Accuracy critical. You need 70% to secure the objective.'}
          </p>

          {/* 数据卡片 */}
          <div className="flex justify-center gap-4 mb-8">
             <div className="bg-slate-50 p-4 rounded-2xl min-w-[110px] border border-slate-100 shadow-sm">
                <div className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Score</div>
                <div className="text-3xl font-black text-purple-600">{score}</div>
             </div>
             <div className="bg-slate-50 p-4 rounded-2xl min-w-[110px] border border-slate-100 shadow-sm">
                <div className="text-xs text-slate-400 uppercase font-bold tracking-wider mb-1">Accuracy</div>
                <div className={`text-3xl font-black ${isSuccess ? 'text-green-500' : 'text-red-500'}`}>
                    {Math.round(accuracy)}%
                </div>
             </div>
          </div>

          {/* 徽章动画 */}
          {newBadges && newBadges.length > 0 && (
              <motion.div 
                initial={{ scale: 0 }} animate={{ scale: 1 }} 
                className="mb-8 bg-yellow-50 border-2 border-yellow-200 p-4 rounded-2xl flex items-center justify-center gap-3 shadow-sm"
              >
                  <div className="text-4xl">{newBadges[0]?.icon || '🏅'}</div>
                  <div className="text-left">
                    <div className="text-xs font-bold text-yellow-600 uppercase">{newBadges.length > 1 ? `${newBadges.length} New Badges!` : 'New Badge!'}</div>
                    <div className="text-yellow-800 font-bold">{newBadges.map(b => b.name).join(', ')}</div>
                  </div>
              </motion.div>
          )}

          {/* 按钮区 */}
          <div className="flex flex-col gap-3">
            {isSuccess ? (
                // ✅ 成功：显示 Next Lesson 和 Back to Menu
                <>
                  <button onClick={onContinue} className="w-full py-4 rounded-xl font-bold text-white shadow-lg shadow-green-200 bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 transition-all transform active:scale-95 flex items-center justify-center text-lg">
                    Next Lesson <AppIcon name="arrow-right" className="ml-2" />
                  </button>
                  {/* 🟢 [修复] 原来用 window.location.href 整页刷新，会把用户踢回登录页 */}
                  <button onClick={onExit} className="text-slate-400 font-bold hover:text-slate-600 text-sm py-2">
                    Back to Course Map
                  </button>
                </>
            ) : (
                // ❌ 失败：主要显示 Retry，次要显示退出
                <>
                  <button onClick={onRetry} className="w-full py-4 rounded-xl font-bold text-white shadow-lg shadow-red-200 bg-gradient-to-r from-red-500 to-rose-600 hover:from-red-600 hover:to-rose-700 transition-all transform active:scale-95 flex items-center justify-center text-lg">
                    <AppIcon name="redo-alt" className="mr-2" /> Try Again
                  </button>
                  <button onClick={onContinue} className="text-slate-400 font-bold hover:text-slate-600 text-sm py-2">
                    Quit to Menu
                  </button>
                </>
            )}
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// ----------------------------------------------------------------------
// 主组件：LessonPage
// ----------------------------------------------------------------------
const LessonPage = () => {
  const { lessonId } = useParams();
  const navigate = useNavigate();
  //Next lesson ID
  const [nextLessonId, setNextLessonId] = useState(null);
  const { user, reloadUser } = useAuth();
  
  const [lessonData, setLessonData] = useState(null); 
  const [lessonWords, setLessonWords] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  
  const [currentStep, setCurrentStep] = useState('flashcards');
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);
  const [selectedSynonyms, setSelectedSynonyms] = useState([]);
  
  // 🟢 Feature 1: 卡片翻转状态
  const [isCardFlipped, setIsCardFlipped] = useState(false);
  
  const [optionsCache, setOptionsCache] = useState({});
  const [synonymChoicesCache, setSynonymChoicesCache] = useState({});
  
  const [showImageModal, setShowImageModal] = useState(false);
  const [modalImageSrc, setModalImageSrc] = useState('');

  // 🟢 [新增状态]
  const startTimeRef = useRef(Date.now());
  const [stepScores, setStepScores] = useState({
    'initial-quiz': 0,
    'vocabulary-master': 0,
    'synonym-scholar': 0
  });
  const [completionResult, setCompletionResult] = useState(null); // 控制弹窗显示
  const queryClient = useQueryClient(); // 🟢 [P2] 用于结算后主动失效进度缓存

  const steps = [
    { id: 'flashcards', name: 'Flash Cards', icon: '📚' },
    { id: 'initial-quiz', name: 'Initial Quiz', icon: '❓' },
    { id: 'vocabulary-master', name: 'Vocabulary Master', icon: '🎯' },
    { id: 'synonym-scholar', name: 'Synonym Scholar', icon: '🔄' },
    { id: 'final-quiz', name: 'Final Quiz', icon: '✅' }
  ];

  // 🟢 1. 使用 useRef 定义音频对象 (保证组件重新渲染时不会销毁)
  const successAudioRef = useRef(null);
  const failAudioRef = useRef(null);

  // 🟢 [修改] 自动查找下一课的 ID (支持跨等级跳转)
  // 逻辑：
  // 1. 先找当前等级的下一课 (current + 1)
  // 2. 如果没找到，找下一个等级的第 1 课
  // 3. 如果是 Genius 等级且没课了，置空 (返回 CoursePage)
  useEffect(() => {
    if (!lessonData || !lessonData.level) return;

    const findNextLesson = async () => {
      try {
        const levels = ['core', 'stretch', 'genius'];
        const currentLevel = lessonData.level.toLowerCase();
        
        // --- 步骤 1: 尝试在当前等级找下一课 ---
        let response = await apiService.getLessons({ level: currentLevel });
        let allLessons = response.lessons || [];
        //console.log(`🔍 [NextLesson] Start finding next for: Level=${currentLevel}, Num=${lessonData.lessonNumber}`);

        // 找到 LessonNumber 比当前大 1 的课程
        let next = allLessons.find(l => l.lessonNumber === lessonData.lessonNumber + 1);

        if (next) {
          setNextLessonId(next.id);
          //console.log(`🔍 [NextLesson] id=${next.id}`);
          return; // 找到了，结束
        }

        // --- 步骤 2: 当前等级没课了，尝试找下一个等级 ---
        const currentIndex = levels.indexOf(currentLevel);
        
        // 只有当不是最后一个等级(genius)时，才去找下一级
        if (currentIndex !== -1 && currentIndex < levels.length - 1) {
            const nextLevel = levels[currentIndex + 1]; // 例如 core -> stretch
            
            // 获取下一等级的课程列表
            response = await apiService.getLessons({ level: nextLevel });
            allLessons = response.lessons || [];

            // 找该等级的第 1 课 (或者 lessonNumber 最小的那一课)
            next = allLessons.find(l => l.lessonNumber === 1);
            
            if (next) {              
                console.log(`New lesson level`);
                setNextLessonId(next.id);
                return; // 找到了跨等级的新课，结束
            }
        }

        // --- 步骤 3: 实在找不到了 (Genius 毕业了)，置空 ---
        setNextLessonId(null); 

      } catch (err) {
        console.error("Failed to find next lesson:", err);
      }
    };

    findNextLesson();
  }, [lessonData]);

  // 🟢 2. 组件挂载时：初始化并预加载音频
  useEffect(() => {
    // 创建对象
    successAudioRef.current = new Audio('/sounds/success.mp3');
    failAudioRef.current = new Audio('/sounds/fail.mp3');
    
    // 设置音量
    successAudioRef.current.volume = 0.5;
    failAudioRef.current.volume = 0.5;

    // 强制预加载 (这就解决了网络延迟问题)
    successAudioRef.current.load();
    failAudioRef.current.load();

    // 组件卸载时清理 (可选，防止内存泄漏)
    return () => {
        successAudioRef.current = null;
        failAudioRef.current = null;
    };
  }, []);

  // 🟢 3. 修改 playSound：直接播放内存中的音频
  const playSound = (type) => {
      try {
          const audio = type === 'success' ? successAudioRef.current : failAudioRef.current;
          if (audio) {
              audio.currentTime = 0; // 如果之前的没播完，重置进度，确保从头播
              audio.play().catch(e => console.log('Audio play failed', e));
          }
      } catch (err) {
          console.log('Audio play error', err);
      }
  };

  // 🟢 [修改] 监听 lessonId 变化，重置状态并重新加载
  useEffect(() => {
    const fetchLesson = async () => {
      try {
        setIsLoading(true);
        const data = await apiService.getLessonById(lessonId);
        setLessonData(data); 
        setLessonWords(data.vocabulary || []);
        
        // 每次切课，先清空下一课ID，等待重新计算
        setNextLessonId(null); 
      } catch (error) {
        console.error('Failed to load lesson:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (user?.uid && lessonId) {
        // 🟢 关键：强制重置所有游戏状态！
        // 否则从 Lesson 1 跳到 Lesson 2 时，currentStep 可能还停留在 'final-quiz'
        setCurrentStep('flashcards');
        resetLocalState(); // 你代码里已经定义了这个函数，直接调用

        // 加载新数据
        fetchLesson();
    }
    // 🟢 [修复] 只依赖 user?.uid，不依赖整个 user 对象。否则做完课程调用 reloadUser()
    // (或答题中途保存资料) 会换掉 user 引用 → 本 effect 重跑 → 清空答题进度/销毁完成弹窗。
  }, [lessonId, user?.uid]); // 👈 依赖项必须包含 lessonId

  // 切换单词时重置翻转状态
  useEffect(() => {
    setIsCardFlipped(false);
  }, [currentWordIndex]);

  // 🟢 Feature: 图片预加载机制
  // 当 currentWordIndex 变化时，立即静默加载当前图片 + 下一张图片
  useEffect(() => {
    if (!lessonWords || lessonWords.length === 0) return;

    const preloadImage = (url) => {
      const img = new Image();
      img.src = url;
    };

    // 1. 预加载当前图片的 (这样用户翻转时，图片已经在缓存里了)
    const currentImgUrl = getImgUrl(lessonWords[currentWordIndex].imageUrl) || "/images/vocabulary-illustration.jpg";
    preloadImage(currentImgUrl);

    // 2. 预加载下一张 (这样点击 Next 时，图片已经在本地了)
    if (currentWordIndex < lessonWords.length - 1) {
      const nextImgUrl = getImgUrl(lessonWords[currentWordIndex + 1].imageUrl) || "/images/vocabulary-illustration.jpg";
      preloadImage(nextImgUrl);
    }
  }, [currentWordIndex, lessonWords]);

  const generateOptions = (correctAnswer, type = 'definition', stepId = '', wordIndex = 0) => {
    const cacheKey = `${stepId}-${wordIndex}`;
    if (optionsCache[cacheKey]) return optionsCache[cacheKey];
    if (!lessonWords || lessonWords.length === 0) return [];
    const allWords = lessonWords.filter(w => w.id !== correctAnswer.id);
    const wrongOptions = allWords.sort(() => Math.random() - 0.5).slice(0, 3).map(w => type === 'definition' ? w.definition : w.word);
    const correctOption = type === 'definition' ? correctAnswer.definition : correctAnswer.word;
    const options = [correctOption, ...wrongOptions].sort(() => Math.random() - 0.5);
    setOptionsCache(prev => ({ ...prev, [cacheKey]: options }));
    return options;
  };

  const generateSynonymChoices = (word) => {
    if (!word || !word.synonyms) return [];
    if (synonymChoicesCache[word.id]) return synonymChoicesCache[word.id];
    if (!lessonWords || lessonWords.length === 0) return [];
    const correctSynonyms = word.synonyms.slice(0, 3);
    const wrongOptions = lessonWords.filter(w => w.id !== word.id && !correctSynonyms.some(syn => syn.toLowerCase() === w.word.toLowerCase())).sort(() => Math.random() - 0.5).slice(0, 5).map(w => w.word);
    const choices = [...correctSynonyms, ...wrongOptions].sort(() => Math.random() - 0.5);
    setSynonymChoicesCache(prev => ({ ...prev, [word.id]: choices }));
    return choices;
  };

  const speakWord = (word, rate = 0.9) => { // 默认语速稍微放慢一点点，适合儿童
  if ('speechSynthesis' in window) {
    // 1. 🛑 播放前先停止之前的发音，防止连点导致声音重叠
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(word);
    
    // 2. 🇬🇧 强制设置语言为英式英语 (如果是美式，用 'en-US')
    utterance.lang = 'en-GB'; 
    utterance.rate = rate; 
    utterance.pitch = 1.1; // 稍微提高一点音调，对儿童更亲切

    // 3. 🧠 尝试选择更高质量的系统语音 (如 Google UK English Female, Apple Daniel 等)
    const voices = window.speechSynthesis.getVoices();
    // 优先找含有 'Google' 或 'Premium' 的英式发音
    const preferredVoice = voices.find(v => 
      (v.lang === 'en-GB' && (v.name.includes('Google') || v.name.includes('Premium'))) || 
      v.lang === 'en-GB'
    );
    
    if (preferredVoice) {
      utterance.voice = preferredVoice;
    }

    window.speechSynthesis.speak(utterance);
  }
};

// 💡 注意：getVoices 是异步加载的，通常需要在 useEffect 里预加载一次
useEffect(() => {
    const loadVoices = () => { window.speechSynthesis.getVoices(); };
    loadVoices();
    if (window.speechSynthesis.onvoiceschanged !== undefined) {
        window.speechSynthesis.onvoiceschanged = loadVoices;
    }
}, []);


  const handleAnswer = (selectedAnswer) => {
    const currentWord = lessonWords[currentWordIndex];
    const correct = selectedAnswer === currentWord.definition;
    // 🎵 [新增] 播放音效
    playSound(correct ? 'success' : 'fail');

    setAnswers(prev => ({...prev, [currentWordIndex]: { answer: selectedAnswer, correct: correct }}));
    
    // 🟢 Feature 5: 奖励 5 倍 (从 +1 改为 +5)
    if (correct) setScore(prev => prev + 5);
    
    setIsCorrect(correct);
    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
      if (currentWordIndex < lessonWords.length - 1) setCurrentWordIndex(prev => prev + 1);
      else setShowResult(true);
    }, 3000);
  };

  const handleSynonymSelect = (word) => {
    if (showFeedback) return;
    setSelectedSynonyms(prev => prev.includes(word) ? prev.filter(w => w !== word) : [...prev, word]);
  };

  const checkSynonyms = () => {
    const currentWord = lessonWords[currentWordIndex];
    const correctSynonyms = currentWord.synonyms || [];
    const correctCount = selectedSynonyms.filter(word => correctSynonyms.some(syn => syn.toLowerCase() === word.toLowerCase())).length;
    const isCorrect = correctCount === correctSynonyms.length && selectedSynonyms.length === correctSynonyms.length;
    
    // 🎵 [新增] 播放音效
    playSound(isCorrect ? 'success' : 'fail');

    setAnswers(prev => ({...prev, [currentWordIndex]: { answer: selectedSynonyms, correct: isCorrect }}));
    
    // 🟢 Feature 5: 奖励 5 倍
    if (isCorrect) setScore(prev => prev + 5);
    
    setIsCorrect(isCorrect);
    setShowFeedback(true);
    setTimeout(() => {
      setShowFeedback(false);
      setSelectedSynonyms([]);
      if (currentWordIndex < lessonWords.length - 1) setCurrentWordIndex(prev => prev + 1);
      else setShowResult(true);
    }, 3000);
  };

  const nextStep = async (finalScoreOverride = null) => {
    // 使用传入的实时分数，如果没有传入则使用当前 state
    const currentScore = finalScoreOverride !== null ? finalScoreOverride : score;

    // 1. 如果不是最后一步，记录分项分数
    if (currentStep !== 'flashcards' && currentStep !== 'final-quiz') {
       setStepScores(prev => ({ ...prev, [currentStep]: currentScore }));
    }

    const currentStepIndex = steps.findIndex(s => s.id === currentStep);
    
    // 🟢 判断逻辑：是“下一步”还是“结束课程”？
    if (currentStepIndex < steps.length - 1) {
      // === Case A: 进入下一个环节 (如从 Quiz 到 Master) ===
      setCurrentStep(steps[currentStepIndex + 1].id);
      
      // 重置状态以备下一环节使用
      setCurrentWordIndex(0);
      setScore(0);
      setAnswers({});
      setShowResult(false);
      setShowFeedback(false);
      setIsCorrect(false);
      setSynonymChoicesCache({});
    } else {
      // === Case B: 课程全部结束 (Final Quiz 完成) ===
      // 🟢 1. 安全检查：防止 lessonData 为空导致发给后端 lessonNumber: 0
      if (!lessonData || typeof lessonData.lessonNumber !== 'number') {
          alert("Error: Lesson data missing. Please refresh and try again.");
          return;
      }

      const finalQuizScore = currentScore; // 使用实时计算的总分
      
      // 计算满分 (假设每题5分)
      const maxScore = lessonWords.length * 5; 
      const accuracy = maxScore > 0 ? (finalQuizScore / maxScore) * 100 : 0;
      const timeSpent = Date.now() - startTimeRef.current;
      const isPassed = accuracy >= 70;

      // 🎵 播放音效
      playSound(isPassed ? 'success' : 'fail');

      let newBadges = [];

      // 提交成绩
      if (isPassed) {
        try {
          setIsLoading(true); // 短暂显示 loading，或者静默提交
          const payload = {
            lessonId: lessonId,
            lessonNumber: lessonData?.lessonNumber || 0, 
            level: lessonData?.level || 'core',          
            scores: {
              initialQuiz: stepScores['initial-quiz'] || 0,
              vocabularyMaster: stepScores['vocabulary-master'] || 0,
              synonymScholar: stepScores['synonym-scholar'] || 0,
              finalQuiz: finalQuizScore
            },
            accuracy: accuracy,
            timeSpent: timeSpent,
            totalWords: lessonWords.length
          };

          // 🟢 [修复] 后端事务返回最新累计值 (totalLessonsCompleted / totalWordsLearned)
          // 旧逻辑从 users 文档读 totalLessonsCompleted，但该字段只存在于 userProgress，
          // 永远是 0，导致徽章从来发不出去。
          const progressRes = await apiService.updateLessonProgress(payload);
          // 🟢 [P2] 结算后主动失效缓存：Courses/Progress 立即反映最新进度、解锁与徽章
          queryClient.invalidateQueries({ queryKey: ['progress'] });
          queryClient.invalidateQueries({ queryKey: ['lessons'] });
          queryClient.invalidateQueries({ queryKey: ['badges'] });
          // 1. 刷新用户信息 (更新积分/streak 显示)
          await reloadUser();

          // 2. 🟢 检查 "学习单词数" 徽章 — 用累计单词数，而不是本课单词数
          const totalWords = progressRes?.totalWordsLearned ?? lessonWords.length;
          const badgeCheckRes1 = await apiService.checkBadges('words_learned', totalWords);
          if (badgeCheckRes1.newBadges) newBadges.push(...badgeCheckRes1.newBadges);

          // 3. 🟢 检查 "完成课程数" 徽章 — 用后端返回的累计课程数
          const currentTotalLessons = progressRes?.totalLessonsCompleted ?? 0;
          const badgeCheckRes2 = await apiService.checkBadges('lessons_completed', currentTotalLessons);
          if (badgeCheckRes2.newBadges) newBadges.push(...badgeCheckRes2.newBadges);
          
          // 🟢 关键：设置 completionResult 以触发弹窗显示
          // 注意：这里不要调用 setShowResult(true)，否则底下的 renderResultScreen 会渲染空 div 导致白屏
          setCompletionResult({
              success: isPassed,
              score: finalQuizScore,
              accuracy: accuracy,
              newBadges: newBadges
          });
          
        } catch (error) {
          console.error("Submission error:", error);
        } finally {
          setIsLoading(false);
        }
      }else {
        // 失败的情况 (未通过)
        setCompletionResult({
            success: false,
            score: finalQuizScore,
            accuracy: accuracy,
            newBadges: []
        });
      }
      
    }
  };

  const resetLocalState = () => {
    setCompletionResult(null); // 关闭弹窗
    setCurrentWordIndex(0);
    setScore(0);
    setAnswers({});
    setShowResult(false);
    setShowFeedback(false);
    setIsCorrect(false);
    setSynonymChoicesCache({});
    setOptionsCache({});
  };

  // 🟢 2. 修正导航切换逻辑 (点击顶部菜单)
  const navigateToStep = (stepId) => {
    setCurrentStep(stepId); // 跳转到点击的步骤
    resetLocalState();      // 仅重置题目状态
  };
  
  // 🟢 3. 修正重试逻辑 (弹窗点击 Retry)
  const retryQuiz = () => {
    setCurrentStep('final-quiz'); // 强制跳回 Final Quiz
    resetLocalState();            // 重置题目状态
  };

  // ----------------------------------------------------------------------
  // 🟢 Flash Cards 渲染 (重构版：正面纯文字详情，反面纯图片)
  // ----------------------------------------------------------------------
  const renderFlashCards = () => (
    <div className="text-center w-full">
      <h2 className="text-3xl font-bold text-gray-800 mb-2">Flash Cards 📚</h2>
      <p className="text-gray-500 mb-8 text-sm font-medium uppercase tracking-wide">
        Tap the card to reveal the illustration
      </p>
      
      {currentWord && (
        <div className="max-w-4xl mx-auto relative perspective-1000 min-h-[500px]">
          <motion.div
            className="relative w-full h-full"
            initial={false}
            animate={{ rotateY: isCardFlipped ? 180 : 0 }}
            transition={{ duration: 0.6, type: "spring", stiffness: 260, damping: 20 }}
            style={{ transformStyle: 'preserve-3d', minHeight: '500px' }}
            onClick={() => setIsCardFlipped(!isCardFlipped)}
          >
            {/* ==============================
                1. 正面：单词详情 (纯文字 UI) 
               ============================== */}
            <div 
              className="absolute inset-0 backface-hidden bg-white rounded-3xl p-8 shadow-2xl flex flex-col justify-between border-b-8 border-purple-100 cursor-pointer hover:border-purple-200 transition-colors"
              style={{ backfaceVisibility: 'hidden' }}
            >
              {/* Top: Counter */}
              <div className="text-center text-gray-300 text-xs font-bold uppercase tracking-widest mb-4">
                  Card {currentWordIndex + 1} of {lessonWords.length}
              </div>

              {/* Header: Word & Audio */}
              <div className="text-center mb-8">
                  <div className="flex items-center justify-center gap-4 mb-2">
                      <h3 className="text-6xl font-extrabold text-slate-800 tracking-tight">{currentWord.word}</h3>
                      <button 
                        onClick={(e) => {
                          e.stopPropagation(); // 防止点击喇叭时翻转
                          speakWord(currentWord.word);
                        }} 
                        className="bg-blue-100 hover:bg-blue-200 text-blue-600 w-12 h-12 rounded-full shadow-sm flex items-center justify-center transition-transform transform active:scale-95"
                      >
                          <AppIcon name="volume-up" className="text-xl" />
                      </button>
                  </div>
                  <div className="text-gray-400 italic text-xl font-serif">
                      /{currentWord.phonetic || currentWord.word}/ 
                      <span className="mx-3 text-gray-300">|</span> 
                      <span className="text-purple-500 font-semibold uppercase text-sm tracking-wider">{currentWord.partOfSpeech || 'noun'}</span>
                  </div>
              </div>

              {/* Middle: Definition & Synonyms (Grid Layout) */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8 text-left">
                  {/* Definition Box */}
                  <div className="bg-green-50/50 border-l-4 border-green-500 p-6 rounded-r-xl flex flex-col justify-center">
                      <div className="flex items-center text-green-700 font-bold mb-3 uppercase text-xs tracking-wider">
                          <AppIcon name="book-open" className="mr-2" /> Definition
                      </div>
                      <p className="text-gray-800 text-lg font-medium leading-relaxed">
                          {currentWord.definition}
                      </p>
                  </div>

                  {/* Synonyms Box */}
                  <div className="bg-orange-50/50 border-l-4 border-orange-400 p-6 rounded-r-xl flex flex-col justify-center">
                      <div className="flex items-center text-orange-600 font-bold mb-3 uppercase text-xs tracking-wider">
                          <AppIcon name="clone" className="mr-2" /> Synonyms
                      </div>
                      <div className="flex flex-wrap gap-2">
                          {currentWord.synonyms && currentWord.synonyms.length > 0 ? (
                              currentWord.synonyms.map((syn, i) => (
                                  <span key={i} className="bg-white border border-orange-200 text-orange-700 px-3 py-1.5 rounded-lg text-sm font-bold shadow-sm">
                                      {syn}
                                  </span>
                              ))
                          ) : (
                              <span className="text-gray-400 italic text-sm">No synonyms available</span>
                          )}
                      </div>
                  </div>
              </div>

              {/* Bottom: Example Sentence */}
              {currentWord.example && (
                  <div className="bg-indigo-50 rounded-2xl p-6 text-center relative border border-indigo-100 mt-auto">
                      <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-white text-indigo-600 px-4 py-1 rounded-full shadow-sm text-xs font-bold uppercase tracking-wider border border-indigo-100">
                          <AppIcon name="quote-left" className="mr-2" /> Context
                      </div>
                      <p className="text-indigo-900 italic text-xl font-serif leading-relaxed pt-2">
                          "{currentWord.example}"
                      </p>
                  </div>
              )}

              {/* Hint */}
              <div className="mt-6 text-center text-gray-400 text-sm animate-pulse">
                  <AppIcon name="sync-alt" className="mr-2" /> Tap card to see illustration
              </div>
            </div>

            {/* ==============================
                2. 反面：图片 (完整显示 + 淡色虚线背景)
               ============================== */}
            <div 
              className="absolute inset-0 backface-hidden bg-white rounded-3xl shadow-2xl cursor-pointer p-6" 
              style={{ transform: 'rotateY(180deg)', backfaceVisibility: 'hidden' }}
            >
               {/* 🟢 修改：外层是淡色虚线框，内层是完整显示的图片 */}
               <div className="w-full h-full bg-slate-50 rounded-2xl border-2 border-dashed border-slate-300 flex items-center justify-center p-2">
                   <img 
                     // 🟢 关键修改：添加 key，使用 word.id 或 imageUrl
                     // 这强制 React 在切换单词时彻底销毁旧 img，创建新 img，避免视觉残留
                     key={currentWord.id || currentWord.imageUrl}
                     src={getImgUrl(currentWord.imageUrl)} 
                     className="w-full h-full object-contain rounded-lg" 
                     alt={currentWord.word}
                   />
               </div>
            </div>

          </motion.div>

          {/* Navigation Footer */}
          <div className="flex justify-between items-center mt-10">
            <button 
                onClick={() => {
                    setCurrentWordIndex(Math.max(0, currentWordIndex - 1));
                    setIsCardFlipped(false);
                }} 
                disabled={currentWordIndex === 0} 
                className="bg-gray-200 text-gray-600 hover:bg-gray-300 px-6 py-3 rounded-xl font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
               <AppIcon name="chevron-left" className="mr-2" /> Previous
            </button>
            
            {/* Pagination Dots */}
            <div className="flex gap-2">
               {lessonWords.map((_, i) => (
                   <div 
                    key={i} 
                    className={`rounded-full transition-all duration-300 ${
                        i === currentWordIndex 
                        ? 'bg-purple-600 w-3 h-3 scale-110' 
                        : 'bg-white/50 w-2 h-2'
                    }`}
                   ></div>
               ))}
            </div>

            {currentWordIndex < lessonWords.length - 1 ? (
              <button 
                onClick={() => {
                    setCurrentWordIndex(currentWordIndex + 1);
                    setIsCardFlipped(false);
                }} 
                className="bg-purple-600 hover:bg-purple-700 text-white px-8 py-3 rounded-xl font-bold shadow-lg transition-all transform active:scale-95 flex items-center"
              >
                Next <AppIcon name="chevron-right" className="ml-2" />
              </button>
            ) : (
              <button 
                onClick={nextStep} 
                className="bg-green-500 hover:bg-green-600 text-white px-8 py-3 rounded-xl font-bold shadow-lg transition-all transform active:scale-95 flex items-center"
              >
                Continue <AppIcon name="check" className="ml-2" />
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );

  // ... (renderInitialQuiz, renderVocabularyMaster, renderSynonymScholar 逻辑保持不变，略)
  const renderInitialQuiz = () => (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Context Clue Detective 🕵️‍♂️</h2>
      {!showResult && currentWord ? (
        <motion.div key={currentWordIndex} initial={{ x: 100, opacity: 0 }} animate={{ x: 0, opacity: 1 }} exit={{ x: -100, opacity: 0 }} className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8">
          <div className="mb-6"><div className="text-sm text-gray-700 mb-2">Word {currentWordIndex + 1} of {lessonWords.length}</div><div className="w-full bg-white bg-opacity-20 rounded-full h-2 mb-4"><div className="bg-blue-500 h-2 rounded-full transition-all duration-300" style={{ width: `${((currentWordIndex + 1) / lessonWords.length) * 100}%` }}></div></div></div>
          <div className="text-2xl font-bold text-gray-800 mb-4">"{currentWord.example}"</div>
          <div className="text-xl text-blue-600 mb-6">What does "<strong>{currentWord.word}</strong>" mean?</div>
          <div className="grid grid-cols-1 gap-4">{generateOptions(currentWord, 'definition', 'initial-quiz', currentWordIndex).map((option, index) => (<motion.button key={index} whileHover={{ scale: showFeedback ? 1 : 1.02 }} whileTap={{ scale: showFeedback ? 1 : 0.98 }} onClick={() => !showFeedback && handleAnswer(option)} disabled={showFeedback} className={`font-semibold py-4 px-6 rounded-lg transition-all duration-200 cursor-pointer ${showFeedback ? option === currentWord.definition ? 'bg-green-500 text-white' : answers[currentWordIndex]?.answer === option ? 'bg-red-500 text-white' : 'bg-gray-300 text-gray-600' : 'bg-white text-purple-600 hover:bg-gray-100'}`}>{option}</motion.button>))}</div>
          <AnimatePresence>{showFeedback && (<motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -20 }} className={`mt-6 p-4 rounded-lg text-center ${isCorrect ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}><div className="text-3xl mb-2">{isCorrect ? '🎉' : '❌'}</div><div className="text-xl font-bold mb-2">{isCorrect ? 'Correct!' : 'Incorrect!'}</div></motion.div>)}</AnimatePresence>
        </motion.div>
      ) : renderResultScreen()}
    </div>
  );

  const renderVocabularyMaster = () => (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">🎯 Vocabulary Master</h2>
      {!showResult && currentWord ? (
        <motion.div key={currentWordIndex} className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8">
          {/* 🟢 新增：美观的进度指示器 */}
          <div className="mb-8 w-full max-w-md mx-auto">
             <div className="flex justify-between items-center mb-2 px-1">
                <span className="bg-white/60 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-extrabold text-purple-800 uppercase tracking-wide shadow-sm border border-white/40">
                   Question {currentWordIndex + 1} / {lessonWords.length}
                </span>
                <span className="text-xs font-bold text-purple-700/70">
                   {Math.round(((currentWordIndex + 1) / lessonWords.length) * 100)}% Complete
                </span>
             </div>
             <div className="h-2.5 w-full bg-white/40 rounded-full overflow-hidden shadow-inner border border-white/20">
                <div 
                  className="h-full bg-gradient-to-r from-purple-500 to-indigo-500 rounded-full transition-all duration-500 ease-out shadow-sm" 
                  style={{ width: `${((currentWordIndex + 1) / lessonWords.length) * 100}%` }}
                />
             </div>
          </div>
          {/* 🟢 结束新增 */}

          <div className="text-5xl font-bold text-purple-600 mb-4">{currentWord.word}</div>
          <button onClick={() => speakWord(currentWord.word)} className="bg-blue-500 text-white font-bold py-2 px-6 rounded-full mb-6">Listen</button>
          <div className="grid grid-cols-1 gap-4">{generateOptions(currentWord, 'definition', 'vocabulary-master', currentWordIndex).map((option, index) => (<button key={index} onClick={() => !showFeedback && handleAnswer(option)} className={`font-semibold py-4 px-6 rounded-lg ${showFeedback ? (option === currentWord.definition ? 'bg-green-500 text-white' : answers[currentWordIndex]?.answer === option ? 'bg-red-500 text-white' : 'bg-white') : 'bg-white text-purple-600'}`}>{option}</button>))}</div>
          {showFeedback && <div className={`mt-4 p-4 rounded text-white ${isCorrect ? 'bg-green-500' : 'bg-red-500'}`}>{isCorrect ? 'Correct!' : `Answer: ${currentWord.definition}`}</div>}
        </motion.div>
      ) : renderResultScreen()}
    </div>
  );

  const renderSynonymScholar = () => (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">🔄 Synonym Scholar</h2>
      {!showResult && currentWord ? (
        <motion.div key={currentWordIndex} className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8">
          {/* 🟢 新增：美观的进度指示器 (配色改为蓝色系) */}
          <div className="mb-8 w-full max-w-md mx-auto">
             <div className="flex justify-between items-center mb-2 px-1">
                <span className="bg-white/60 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-extrabold text-blue-800 uppercase tracking-wide shadow-sm border border-white/40">
                   Question {currentWordIndex + 1} of {lessonWords.length}
                </span>
                <span className="text-xs font-bold text-blue-700/70">
                   {Math.round(((currentWordIndex + 1) / lessonWords.length) * 100)}%
                </span>
             </div>
             <div className="h-2.5 w-full bg-white/40 rounded-full overflow-hidden shadow-inner border border-white/20">
                <div 
                  className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full transition-all duration-500 ease-out shadow-sm" 
                  style={{ width: `${((currentWordIndex + 1) / lessonWords.length) * 100}%` }}
                />
             </div>
          </div>
          {/* 🟢 结束新增 */}
          
          <h3 className="text-2xl font-bold mb-4">Find synonyms for: {currentWord.word}</h3>
          <div className="grid grid-cols-2 gap-4 mb-6">{generateSynonymChoices(currentWord).map((word, index) => (<button key={index} onClick={() => handleSynonymSelect(word)} className={`py-3 px-4 rounded-lg font-semibold border-2 ${showFeedback ? (currentWord.synonyms?.some(s => s.toLowerCase() === word.toLowerCase()) ? 'bg-green-500 text-white' : selectedSynonyms.includes(word) ? 'bg-red-500 text-white' : 'bg-gray-200') : (selectedSynonyms.includes(word) ? 'bg-blue-500 text-white' : 'bg-white')}`}>{word}</button>))}</div>
          {!showFeedback && <button onClick={checkSynonyms} className="bg-green-500 text-white py-2 px-8 rounded-full font-bold">Check</button>}
          {showFeedback && <div className="mt-4 font-bold">{isCorrect ? 'Perfect!' : `Correct synonyms: ${currentWord.synonyms?.join(', ')}`}</div>}
        </motion.div>
      ) : renderResultScreen()}
    </div>
  );

  const renderFinalQuiz = () => (
    <div className="text-center">
      <h2 className="text-3xl font-bold text-gray-800 mb-6">Final Quiz - Spelling</h2>
      
      {/* 只有当 currentWord 存在且没有显示结果时渲染 */}
      {currentWord ? (
        <motion.div key={currentWordIndex} className="bg-white bg-opacity-20 backdrop-blur-lg rounded-2xl p-8">
          
          {/* 进度条 */}
          <div className="mb-8 w-full max-w-md mx-auto">
             <div className="flex justify-between items-center mb-2 px-1">
                <span className="bg-white/60 backdrop-blur-md px-3 py-1 rounded-lg text-xs font-extrabold text-green-800 uppercase tracking-wide shadow-sm border border-white/40">
                   Word {currentWordIndex + 1} / {lessonWords.length}
                </span>
                <span className="text-xs font-bold text-green-700/70">
                   Almost there!
                </span>
             </div>
             <div className="h-2.5 w-full bg-white/40 rounded-full overflow-hidden shadow-inner border border-white/20">
                <div 
                  className="h-full bg-gradient-to-r from-green-400 to-emerald-600 rounded-full transition-all duration-500 ease-out shadow-sm" 
                  style={{ width: `${((currentWordIndex + 1) / lessonWords.length) * 100}%` }}
                />
             </div>
          </div>

          <div className="text-lg text-blue-800 mb-6 font-bold">{currentWord.definition}</div>
          
          <SpellingInterface 
            word={currentWord.word} 
            onComplete={(correct) => {
              // 🟢 1. 计算这一题之后的总分 (本地计算，解决 State 延迟)
              let currentTotalScore = score;
              if (correct) {
                  currentTotalScore += 5;
                  setScore(prev => prev + 5); // 依然更新 State 以保持 UI 同步
              }
              
              setAnswers(prev => ({...prev, [currentWordIndex]: {answer: currentWord.word, correct}}));

              // 🟢 2. 判断是下一题还是结束
              if (currentWordIndex < lessonWords.length - 1) {
                  // 还有下一题
                  setCurrentWordIndex(prev => prev + 1);
              } else {
                  // 🟢 3. 最后一题完成：直接进入结算，并传入刚才算好的总分
                  // 千万不要调用 setShowResult(true)，那会导致白屏
                  nextStep(currentTotalScore); 
              }
            }} 
          />
        </motion.div>
      ) : (
        // 加载占位符
        <div className="text-white font-bold animate-pulse">Loading...</div>
      )}
    </div>
  );

  const renderResultScreen = () => <div />;

  if (isLoading) return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-sky-100 to-blue-300"><div className="text-2xl text-blue-600 font-bold">Loading Lesson...</div></div>;
  if (!lessonWords || lessonWords.length === 0) return <div className="min-h-screen p-6"><div className="max-w-4xl mx-auto text-center"><h1 className="text-4xl font-bold">Lesson Not Found</h1><button onClick={() => navigate('/courses')} className="bg-white px-6 py-2 rounded-lg mt-4">Back</button></div></div>;
const currentWord = lessonWords[currentWordIndex];

  return (
    <div className="min-h-screen bg-gradient-to-br from-sky-100 via-sky-200 to-blue-300 p-6">
      {/* 🟢 插入结算弹窗 */}
      <AnimatePresence>
        {completionResult && (
            <CompletionModal 
                isSuccess={completionResult.success}
                score={completionResult.score}
                accuracy={completionResult.accuracy}
                newBadges={completionResult.newBadges}
                onRetry={retryQuiz}
                onExit={() => navigate('/courses')}
                // 🟢 [修改] 智能跳转逻辑
                onContinue={() => {
                    if (nextLessonId) {
                        // 如果有下一课，直接跳过去
                        navigate(`/lesson/${nextLessonId}`);
                    } else {
                        // 如果没有（全通关），才回课程表
                        navigate('/courses');
                    }
                }}
            />
        )}
      </AnimatePresence>

      <div className="max-w-5xl mx-auto">
        {/* Header Steps - 🟢 修复了 Icon 显示 */}
        <div className="text-center mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-6 drop-shadow-md tracking-wide">
             {lessonData ? lessonData.title : `Lesson ${lessonId}`}
          </h1>
          <div className="flex justify-center flex-wrap gap-3">
            {steps.map(step => (
              <button 
                key={step.id} 
                onClick={() => navigateToStep(step.id)}
                className={`px-5 py-2.5 rounded-full text-sm font-bold shadow-sm transition-all duration-200 flex items-center gap-2
                  ${step.id === currentStep 
                    ? 'bg-white text-purple-600 scale-105 shadow-md ring-2 ring-purple-200' 
                    : 'bg-white/30 text-slate-700 hover:bg-white/50 backdrop-blur-sm'}`}
              >
                {/* 🟢 关键修复：显式渲染 Icon */}
                <span>{step.icon}</span>
                {step.name}
              </button>
            ))}
          </div>
        </div>

        <AnimatePresence mode="wait">
          {currentStep === 'initial-quiz' && renderInitialQuiz()}
          {currentStep === 'flashcards' && renderFlashCards()}
          {currentStep === 'vocabulary-master' && renderVocabularyMaster()}
          {currentStep === 'synonym-scholar' && renderSynonymScholar()}
          {currentStep === 'final-quiz' && renderFinalQuiz()}
        </AnimatePresence>

        <div className="text-center mt-12">
          <button onClick={() => navigate('/courses')} className="text-white/80 hover:text-white font-bold transition-colors">
             <AppIcon name="arrow-left" className="mr-2" /> Back to Courses
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showImageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50 backdrop-blur-sm" onClick={() => setShowImageModal(false)}>
            <div className="bg-white p-2 rounded-xl max-w-4xl max-h-[90vh] shadow-2xl">
              <img src={modalImageSrc} className="w-full h-full object-contain rounded-lg" />
            </div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LessonPage;